// Shows a timestamp as "5m ago / 2h ago / 3d ago" with the full local
// date+time available on hover via the native title tooltip.
//
// Why this exists: absolute timestamps like "8 Jun 2026, 14:32" are
// precise but hard to skim — you have to mentally diff them against
// "now" to know whether something happened five minutes ago or
// yesterday. Relative is the inverse: skimmable instantly but loses
// precision. Showing the relative form as the primary label and the
// absolute in the tooltip gives both — same pattern as Slack, Linear,
// GitHub, Gmail. Hover for precision, glance for recency.
//
// Live-updates every 60s so a "just now" bubble eventually becomes
// "1m ago" → "5m ago" without a route navigation. One module-level
// `tick` counter wakes every subscribed component on the same minute
// boundary so we don't burn N timers for N rendered timestamps.
//
// Timezone: the title tooltip uses formatDateTime() which renders in
// the VIEWER's local TZ (see lib/format.ts). Same applies if the
// caller passes their own `titleFormatter` — we don't override it.

import { useEffect, useState } from 'react';
import { compactDate, humaniseDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  /** ISO string, Date, or nullish. Nullish renders an em-dash. */
  value: string | Date | null | undefined;
  /** Optional className passed through to the <time> element. */
  className?: string;
  /** Override how the tooltip absolute time is formatted. Defaults to
   *  `formatDateTime` (user-local). Useful if a caller needs a longer
   *  format (e.g. include seconds or weekday) without forking. */
  titleFormatter?: (value: string | Date) => string;
  /** Render the ultra-short form ("2h", "13 Aug") instead of the prose form
   *  ("2h ago", "13 Aug 2026, 03:35 pm"). For dense list rows where a wrapping
   *  timestamp breaks the row height. */
  compact?: boolean;
}

// ───── Shared ticker: one timer wakes every relative timestamp ────────────
//
// useSyncExternalStore-style minimal pub/sub. A single setInterval
// updates `tick` once a minute; each <RelativeTime> subscribes and
// re-renders. Cheaper than N timers and keeps minute rollovers
// visually consistent across the page.

let tick = 0;
const subscribers = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

function ensureInterval() {
  if (intervalId !== null) return;
  intervalId = setInterval(() => {
    tick++;
    for (const sub of subscribers) sub();
  }, 60_000);
}

function subscribe(cb: () => void) {
  ensureInterval();
  subscribers.add(cb);
  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0 && intervalId !== null) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };
}

function useTick(): number {
  const [, setLocal] = useState(tick);
  useEffect(() => subscribe(() => setLocal(tick)), []);
  return tick;
}

export function RelativeTime({ value, className, titleFormatter, compact }: Props) {
  useTick(); // re-render on each minute boundary

  if (!value) {
    return <span className={cn('text-muted-foreground', className)}>—</span>;
  }
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return <span className={cn('text-muted-foreground', className)}>—</span>;
  }

  // Compact form for dense list rows — see the note on `compactDate`. The
  // precise datetime still reaches the user via `title` and `dateTime`.
  const relative = compact ? compactDate(date) : humaniseDate(date);
  const absolute = (titleFormatter ?? formatDateTime)(date);

  return (
    <time
      // ISO datetime attr makes the timestamp machine-readable for
      // assistive tech + future scrapers. The visible label stays
      // human-readable; the tooltip carries the precise form.
      dateTime={date.toISOString()}
      title={absolute}
      className={cn('tabular-nums', className)}
    >
      {relative}
    </time>
  );
}
